import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  ticketsApi,
  Ticket,
  CreateTicketDto,
  UpdateTicketDto,
  TicketFilters,
} from '@/lib/api/tickets';

export function useTickets(filters?: TicketFilters) {
  const { t } = useTranslation();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    totalPages: 1,
  });

  const fetchTickets = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await ticketsApi.getAll(filters);
      setTickets(response.data);
      setPagination({
        total: response.total,
        page: response.page,
        totalPages: response.totalPages,
      });
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('tickets.fetchError'));
    } finally {
      setIsLoading(false);
    }
  }, [
    filters?.page,
    filters?.limit,
    filters?.contractId,
    filters?.userId,
    filters?.companyId,
    filters?.startDate,
    filters?.endDate,
    filters?.withHours,
    t,
  ]);

  const createTicket = useCallback(
    async (ticketData: CreateTicketDto) => {
      try {
        setIsSaving(true);
        const newTicket = await ticketsApi.create(ticketData);
        setTickets((prev) => [newTicket, ...prev]);
        toast.success(t('tickets.createSuccess'));
        return true;
      } catch (err: any) {
        toast.error(err.response?.data?.message || t('tickets.createError'));
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [t]
  );

  const updateTicket = useCallback(
    async (id: string, ticketData: UpdateTicketDto) => {
      try {
        setIsSaving(true);
        const updatedTicket = await ticketsApi.update(id, ticketData);
        // Update the ticket in the current list
        setTickets((prev) => prev.map((ticket) => (ticket.id === id ? updatedTicket : ticket)));
        toast.success(t('tickets.updateSuccess'));
        return true;
      } catch (err: any) {
        toast.error(err.response?.data?.message || t('tickets.updateError'));
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [t]
  );

  const deleteTicket = useCallback(
    async (id: string) => {
      try {
        setIsSaving(true);
        await ticketsApi.delete(id);
        // Remove the ticket from the current list
        setTickets((prev) => prev.filter((ticket) => ticket.id !== id));
        toast.success(t('tickets.deleteSuccess'));
        return true;
      } catch (err: any) {
        toast.error(err.response?.data?.message || t('tickets.deleteError'));
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [t]
  );

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  return {
    tickets,
    isLoading,
    isSaving,
    pagination,
    createTicket,
    updateTicket,
    deleteTicket,
    refreshTickets: fetchTickets,
  };
}
